/**
 * CapoHelper.js
 * 
 * This component provides a utility for worship leaders and musicians to transpose songs
 * when using a capo on stringed instruments like guitar. It allows users to:
 * - Select an original key and see all possible capo positions
 * - Choose between major and minor keys
 * - Transpose up or down by a specified number of semitones
 * - View chord charts for both the original and transposed keys
 * 
 * The tool helps musicians play songs in more comfortable keys while maintaining
 * the original tonality, which is particularly useful for vocalists.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/common/PageHeader';
import { NOTES, NOTES_WITH_FLATS, NATURAL_KEYS } from '../utils/musicUtils';

const CapoHelper = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  // eslint-disable-next-line no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [originalKey, setOriginalKey] = useState('C');
  const [keyType, setKeyType] = useState('major');
  const [direction, setDirection] = useState('up');
  const [semitones, setSemitones] = useState(2);
  const [resultantKey, setResultantKey] = useState('D'); // Default to D (C + 2 semitones up)
  const [result, setResult] = useState(null);

  // This useEffect will trigger the calculation once the component is mounted
  // since originalKey and resultantKey are already set with default values
  // The calculation will happen through the other useEffect that depends on these values

  // Calculate the resultant key based on original key, direction, and semitones
  useEffect(() => {
    if (originalKey) {
      const keyIndex = NOTES.indexOf(originalKey);
      if (keyIndex !== -1) {
        let targetIndex;
        if (direction === 'up') {
          targetIndex = (keyIndex + semitones) % 12;
        } else {
          targetIndex = (keyIndex - semitones + 12) % 12;
        }
        setResultantKey(NOTES[targetIndex]);
      }
    } else {
      setResultantKey('');
    }
  }, [originalKey, direction, semitones]);

  // Automatically calculate capo position when any form value changes
  useEffect(() => {
    if (originalKey && resultantKey) {
      calculateCapo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalKey, keyType, direction, semitones, resultantKey]);

  // Update direction and semitones when resultant key changes
  const handleResultantKeyChange = (newKey) => {
    if (!originalKey || newKey === originalKey) {
      setResultantKey(newKey);
      setSemitones(0);
      setDirection('up');
      return;
    }

    const origIndex = NOTES.indexOf(originalKey);
    const newIndex = NOTES.indexOf(newKey);
    if (origIndex === -1 || newIndex === -1) return;

    // Calculate the semitones and direction between the original key and the new key
    let semitonesChange = newIndex - origIndex;
    
    // Normalize to the smallest number of semitones (up or down)
    if (semitonesChange > 4) {
      semitonesChange -= 12;
    } else if (semitonesChange < -4) {
      semitonesChange += 12;
    }

    if (semitonesChange >= 0) {
      setDirection('up');
      setSemitones(semitonesChange);
    } else {
      setDirection('down');
      setSemitones(Math.abs(semitonesChange));
    }
    
    setResultantKey(newKey);
  };

  // Calculate capo position and new key
  const calculateCapo = () => {
    if (!originalKey) return;

    // Find the index of the original key in the NOTES array
    const keyIndex = NOTES.indexOf(originalKey);
    if (keyIndex === -1) return;

    // Calculate the target key index based on direction and semitones
    let targetIndex;
    if (direction === 'up') {
      targetIndex = (keyIndex + semitones) % 12;
    } else {
      targetIndex = (keyIndex - semitones + 12) % 12;
    }
    
    // Find the best capo position that results in a natural key to play
    let bestCapoPosition = -1;
    let bestKeyToPlay = null;
    
    // Always try to find a capo position, even if original key and target key are the same
    // This helps musicians who want to play in familiar chord shapes
    
    // Try different capo positions (1-11) and find the one that results in a natural key
    // Start from 1 to ensure we're always suggesting a capo (not 0 which means no capo)
    for (let capoPos = 1; capoPos < 12; capoPos++) {
      // Calculate what key we would play with this capo position
      const resultantKeyIndex = (targetIndex - capoPos + 12) % 12;
      const keyToPlay = NOTES[resultantKeyIndex];
      
      // Check if this is a natural key (no sharps or flats)
      if (NATURAL_KEYS.includes(keyToPlay)) {
        bestCapoPosition = capoPos;
        bestKeyToPlay = keyToPlay;
        break; // Found a natural key, stop searching
      }
    }
    
    // If no natural key was found, fall back to the standard calculation
    let capoPosition, newKey;
    
    if (bestCapoPosition !== -1) {
      capoPosition = bestCapoPosition;
      newKey = bestKeyToPlay;
    } else {
      // Standard calculation as fallback
      if (direction === 'up') {
        capoPosition = semitones;
      } else {
        capoPosition = 12 - semitones;
        if (capoPosition === 12) capoPosition = 0;
      }
      
      // Get the new key to play with capo - this is what you play when using the capo
      newKey = direction === 'up' 
        ? NOTES[(keyIndex - semitones + 12) % 12] 
        : NOTES[(keyIndex + semitones) % 12];
    }

    // Set the resultant key based on the target index
    const finalResultantKey = NOTES[targetIndex];
    setResultantKey(finalResultantKey);
    
    // Calculate alternative capo position to play in the original key
    let alternativeCapoPosition = null;
    if (originalKey !== newKey) {
      // To play in the original key with capo, calculate the distance between original key and new key
      const originalKeyIndex = NOTES.indexOf(originalKey);
      const newKeyIndex = NOTES.indexOf(newKey);
      
      // Calculate how many semitones we need to go from new key to original key
      // For the alternative capo position when going down, we need to calculate 12 - semitones
      // For going up, we can use the semitones directly
      let initialCapoPosition = direction === 'down' ? 12 - semitones : semitones;
      
      // Try to find an alternative natural key with a comfortable capo position (1-7)
      // This gives the player more options and is especially useful when the main capo position is high
      
      // First, try to find a natural key that produces the correct resultant key with a capo
      let bestKey = originalKey;
      let bestCapoPosition = initialCapoPosition;
      let foundBetterOption = false;
      
      for (const naturalKey of NATURAL_KEYS) {
        const naturalKeyIndex = NOTES.indexOf(naturalKey);
        
        // To get the correct capo position, we need to calculate how many semitones
        // we need to go from the natural key to the resultant key (not the original key)
        // When we place a capo on fret X of key Y, we get a key that's X semitones higher than Y
        const resultantKeyIndex = NOTES.indexOf(resultantKey);
        
        // Calculate how many semitones we need to go UP from natural key to reach resultant key
        const capoPos = (resultantKeyIndex - naturalKeyIndex + 12) % 12;
        
        // We want a capo position that's reasonable (1-5) and produces the correct resultant key
        if (capoPos > 0 && capoPos <= 5) {
          bestKey = naturalKey;
          bestCapoPosition = capoPos;
          foundBetterOption = true;
          break;
        }
      }
      
      // Store the key and capo position
      if (foundBetterOption) {
        alternativeCapoPosition = {
          position: bestCapoPosition,
          key: bestKey
        };
      } else {
        // If we couldn't find a better option, use the original calculation
        alternativeCapoPosition = {
          position: initialCapoPosition,
          key: originalKey
        };
      }
      
      // If alternative capo position is 0, it means no capo is needed
      if (alternativeCapoPosition.position === 0) {
        alternativeCapoPosition = null;
      }
    }
    
    setResult({
      capoPosition,
      newKey,
      keyType,
      resultantKey: finalResultantKey,
      alternativeCapoPosition,
      originalKey
    });
  };

  // Reset the form
  const handleReset = () => {
    setOriginalKey('');
    setKeyType('major');
    setDirection('up');
    setSemitones(2);
    setResultantKey('');
    setResult(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader 
        title={t('capoHelper.title')} 
        subtitle={t('capoHelper.subtitle')}
        icon={<span role="img" aria-label="guitar">🎸</span>}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('capoHelper.form.title')}
            </Typography>
            
            {/* Original Key and Key Type in the same row */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="original-key-label">{t('capoHelper.form.originalKey')}</InputLabel>
                  <Select
                    labelId="original-key-label"
                    value={originalKey}
                    onChange={(e) => setOriginalKey(e.target.value)}
                    label={t('capoHelper.form.originalKey')}
                  >
                    {NOTES_WITH_FLATS.map((note, index) => (
                      <MenuItem key={index} value={NOTES[index]}>{note}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="key-type-label">{t('capoHelper.form.keyType')}</InputLabel>
                  <Select
                    labelId="key-type-label"
                    value={keyType}
                    onChange={(e) => setKeyType(e.target.value)}
                    label={t('capoHelper.form.keyType')}
                  >
                    <MenuItem value="major">{t('capoHelper.form.major')}</MenuItem>
                    <MenuItem value="minor">{t('capoHelper.form.minor')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Direction and Semitones in the same row */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="direction-label">{t('capoHelper.form.direction')}</InputLabel>
                  <Select
                    labelId="direction-label"
                    value={direction}
                    onChange={(e) => setDirection(e.target.value)}
                    label={t('capoHelper.form.direction')}
                  >
                    <MenuItem value="up">{t('capoHelper.form.up')}</MenuItem>
                    <MenuItem value="down">{t('capoHelper.form.down')}</MenuItem>
                  </Select>
                  <FormHelperText sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                    {direction === 'up' 
                      ? t('capoHelper.form.upHelp')
                      : t('capoHelper.form.downHelp')}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="semitones-label">{t('capoHelper.form.semitones')}</InputLabel>
                  <Select
                    labelId="semitones-label"
                    value={semitones}
                    onChange={(e) => setSemitones(Number(e.target.value))}
                    label={t('capoHelper.form.semitones')}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Resultant Key selector */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="resultant-key-label">{t('capoHelper.form.resultantKey')}</InputLabel>
              <Select
                labelId="resultant-key-label"
                value={resultantKey}
                onChange={(e) => handleResultantKeyChange(e.target.value)}
                label={t('capoHelper.form.resultantKey')}
                disabled={!originalKey}
              >
                {NOTES_WITH_FLATS.map((note, index) => (
                  <MenuItem key={index} value={NOTES[index]}>{note}</MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                {t('capoHelper.form.resultantKeyHelp')}
              </FormHelperText>
            </FormControl>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={handleReset}
              >
                {t('capoHelper.form.reset')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          {result && (
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
              color: theme.palette.text.primary,
              boxShadow: 3
            }}>
              <CardContent>
                <Typography variant="h5" gutterBottom align="center" sx={{ color: theme.palette.primary.main }}>
                  {t('capoHelper.result.title')}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 2
                }}>
                  {result.capoPosition === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: 2
                    }}>
                      <Box sx={{ 
                        width: 120, 
                        height: 120, 
                        borderRadius: '50%', 
                        bgcolor: theme.palette.success.light,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                        boxShadow: 2
                      }}>
                        <Typography variant="h6" sx={{ color: theme.palette.success.contrastText }}>
                          {t('capoHelper.result.noCapo')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {t('capoHelper.result.noCapoNeeded')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      {/* Visual representation of a guitar neck with capo */}
                      <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        maxWidth: 300,
                        height: 100,
                        mx: 'auto',
                        mb: 3,
                        mt: 1,
                        pl: 3 /* Add padding to the left to make room for the guitar head */
                      }}>
                        {/* Guitar headstock - main shape (acoustic style) */}
                        <Box sx={{ 
                          position: 'absolute',
                          top: 32,
                          left: -15,
                          width: 30,
                          height: 35,
                          bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#2D2D2D',
                          borderRadius: '4px 0 0 4px',
                          zIndex: 1,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }} />
                        
                        {/* Tuning pegs - top row */}
                        <Box sx={{
                          position: 'absolute',
                          top: 28,
                          left: -13,
                          zIndex: 2,
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '3px'
                        }}>
                          {[...Array(3)].map((_, i) => (
                            <Box key={i} sx={{
                              width: 6,
                              height: 6,
                              bgcolor: theme.palette.mode === 'dark' ? '#616161' : '#424242',
                              borderRadius: '50%',
                              boxShadow: '0 1px 1px rgba(0,0,0,0.2)'
                            }} />
                          ))}
                        </Box>
                        
                        {/* Tuning pegs - bottom row */}
                        <Box sx={{
                          position: 'absolute',
                          top: 65,
                          left: -13,
                          zIndex: 2,
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '3px'
                        }}>
                          {[...Array(3)].map((_, i) => (
                            <Box key={i+3} sx={{
                              width: 6,
                              height: 6,
                              bgcolor: theme.palette.mode === 'dark' ? '#616161' : '#424242',
                              borderRadius: '50%',
                              boxShadow: '0 1px 1px rgba(0,0,0,0.2)'
                            }} />
                          ))}
                        </Box>
                        
                        {/* Nut */}
                        <Box sx={{
                          position: 'absolute',
                          top: 40,
                          left: -1,
                          width: 2,
                          height: 20,
                          bgcolor: theme.palette.mode === 'dark' ? '#E0E0E0' : '#BDBDBD',
                          zIndex: 2
                        }} />
                        
                        {/* Guitar neck */}
                        <Box sx={{ 
                          position: 'absolute',
                          top: 40,
                          left: 0,
                          width: '100%',
                          height: 20,
                          bgcolor: theme.palette.mode === 'dark' ? '#5D4037' : '#8D6E63',
                          borderRadius: 1
                        }} />
                        
                        {/* Frets */}
                        {[...Array(12)].map((_, i) => (
                          <Box key={i} sx={{ 
                            position: 'absolute',
                            top: 38,
                            left: `${(i + 1) * (100 / 13)}%`,
                            width: 2,
                            height: 24,
                            bgcolor: theme.palette.mode === 'dark' ? '#E0E0E0' : '#616161',
                            zIndex: 1
                          }} />
                        ))}
                        
                        {/* Capo */}
                        <Box sx={{ 
                          position: 'absolute',
                          top: 30,
                          left: `${result.capoPosition * (100 / 13)}%`,
                          width: `${(100 / 13)}%`,
                          height: 40,
                          bgcolor: theme.palette.error.main,
                          borderRadius: '4px',
                          zIndex: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: '#fff',
                          fontWeight: 'bold',
                          boxShadow: 3
                        }}>
                          {result.capoPosition}
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        gap: 2,
                        mb: 2
                      }}>
                        <Paper elevation={2} sx={{ 
                          p: 2, 
                          bgcolor: theme.palette.primary.main, 
                          color: theme.palette.primary.contrastText,
                          borderRadius: 2,
                          minWidth: 100,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                            {t('capoHelper.result.capoAt')}
                          </Typography>
                          <Typography variant="h4">
                            {result.capoPosition}
                          </Typography>
                        </Paper>
                        
                        <Paper elevation={2} sx={{ 
                          p: 2, 
                          bgcolor: theme.palette.secondary.main, 
                          color: theme.palette.secondary.contrastText,
                          borderRadius: 2,
                          minWidth: 100,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                            {t('capoHelper.result.playIn')}
                          </Typography>
                          <Typography variant="h4">
                            {NOTES_WITH_FLATS[NOTES.indexOf(result.newKey)]}
                          </Typography>
                          <Typography variant="caption">
                            {t(`capoHelper.form.${result.keyType}`)}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                    {t('capoHelper.result.explanation', { 
                      originalKey: NOTES_WITH_FLATS[NOTES.indexOf(originalKey)], 
                      originalType: t(`capoHelper.form.${keyType}`),
                      targetKey: NOTES_WITH_FLATS[NOTES.indexOf(resultantKey)],
                      targetType: t(`capoHelper.form.${keyType}`)
                    })}
                  </Typography>
                  
                  {/* Alternative capo option */}
                  {result.alternativeCapoPosition && (
                    <Typography variant="body2" sx={{ 
                      mt: 2, 
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      fontStyle: 'italic'
                    }}>
                      {t('capoHelper.result.alternativeOption', { 
                        key: NOTES_WITH_FLATS[NOTES.indexOf(result.alternativeCapoPosition.key)],
                        position: result.alternativeCapoPosition.position
                      })}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {!result && (
            <Paper sx={{ 
              p: 3, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]
            }}>
              <Typography variant="h6" align="center" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                {t('capoHelper.placeholder')}
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CapoHelper;
